import React, {useEffect} from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { PreferencesPage } from '../components';
import { useToastStore } from '../../../redux/toast';
import {
    formFieldInputs as preferencesFormFieldInputs,
    FormFields as PreferencesFormFields,
} from "../../../forms/preferences/preferences.form";
import {useCustomerStore} from "../../../redux/customers";
import {usePreferencesFacade} from "../../../redux/preferences";
import {useHistory} from "react-router";
import {PushNotifications} from "@capacitor/push-notifications";
import {Capacitor} from "@capacitor/core";
import {Device} from "@capacitor/device";
import {FCM} from "@capacitor-community/fcm";
import {useAuthStore} from "../../../redux/auth";
import {ApiResponse} from "../../../models";

type Props = {
};

export const PreferencesPageContainer: React.FC<Props> = () => {
    const isWeb = Capacitor.getPlatform() === 'web';
    const history = useHistory();
    const toastStore = useToastStore();
    const { showError, showSuccess, clear: toastClear } = toastStore;

    const customerStore = useCustomerStore();
    const {
        activeCustomer: customer,
        isFetching: isFetchingCustomer,
    } = customerStore;

    const {
        marketingEmailStatus,
        pushNotificationsStatus,
        isFetching: isFetchingPreferences,
        isSaving,
        fetchPreferences,
        updatePreferences,
        clearErrors: clearPreferenceErrors,
    } = usePreferencesFacade();

    const { deregisterFcmToken, registerFcmToken } = useAuthStore();

    const preferencesForm = useForm<PreferencesFormFields>({
        mode: 'onSubmit',
        defaultValues: {
            marketingEmailStatus: '2',
            pushNotifications: '1',
        }
    });

    const { setValue, clearErrors: formClearErrors, control } = preferencesForm;

    const fetchedForId = React.useRef<number | null>(null);

    const pushNotificationsToggle = useWatch({
        control,
        name: 'pushNotifications',
    });

    useEffect(() => {
        if (customer?.id && fetchedForId.current !== customer.id) {
            // Fire-and-forget: a rejection is already recorded on the slice's
            // errorMessage by the rejected reducer; swallow here so the now-unwrapped
            // promise doesn't surface as an unhandled rejection.
            fetchPreferences(customer.id).catch(() => {});
            fetchedForId.current = customer.id;
        }
    }, [fetchPreferences, customer?.id]);

    useEffect(() => {
        // If marketingEmailStatus is 0 (Not Set), it should be treated as 'Unsubscribed' ('2')
        if (marketingEmailStatus !== undefined) {
            const val = marketingEmailStatus === 0 ? '2' : String(marketingEmailStatus);
            setValue('marketingEmailStatus', val);
        }
    }, [marketingEmailStatus, setValue]);

    // Set dropdown from the backend preference (1 = subscribed, 2 = unsubscribed, 0 = not yet loaded)
    useEffect(() => {
        if (pushNotificationsStatus !== 0) {
            setValue('pushNotifications', String(pushNotificationsStatus));
        }
    }, [pushNotificationsStatus, setValue]);

    // When selecting Subscribed, request OS permission if not already granted
    useEffect(() => {
        const handlePushToggle = async () => {
            if (pushNotificationsToggle !== '1') return;
            if (isWeb) {
                if (Notification.permission !== 'granted') {
                    const permission = await Notification.requestPermission();
                    if (permission !== 'granted') {
                        setValue('pushNotifications', '2');
                    }
                }
            } else {
                const permission = await PushNotifications.checkPermissions();
                if (permission.receive !== 'granted') {
                    const request = await PushNotifications.requestPermissions();
                    if (request.receive !== 'granted') {
                        setValue('pushNotifications', '2');
                    }
                }
            }
        };
        handlePushToggle();
    }, [pushNotificationsToggle, setValue, isWeb]);

    const onSubmitPreferences = async (data: PreferencesFormFields) => {
        await toastClear();

        if (!customer?.id) {
            showError({message: 'Customer not found.'});
            return;
        }

        try {
            const pushStatus = Number(data.pushNotifications);
            await updatePreferences(customer.id, Number(data.marketingEmailStatus), pushStatus);

            if (!isWeb) {
                try {
                    const { token } = await FCM.getToken();
                    if (data.pushNotifications === '1') {
                        const [deviceId, deviceInfo] = await Promise.all([Device.getId(), Device.getInfo()]);
                        await registerFcmToken({ fcmToken: token, deviceUuid: deviceId.identifier, platform: deviceInfo.platform, customerId: customer.id });
                    } else {
                        await deregisterFcmToken({ fcmToken: token });
                    }
                } catch (e) {
                    // FCM unavailable (simulator, web build, etc.)
                }
            }

            showSuccess({message: 'Preferences updated successfully!', duration: 2000}).then(() => {
                history.goBack();
            });
        } catch (e) {
            const errorMessage = (e as ApiResponse | undefined)?.errorMessage;
            showError({message: errorMessage || 'Failed to update preferences.'});
        }
    };

    const onBackClicked = async () => {
        formClearErrors();
        clearPreferenceErrors();
        history.goBack();
    }

    const isFetching = isFetchingCustomer || isFetchingPreferences;

    return (
        <PreferencesPage
            customer={customer}
            preferencesForm={preferencesForm}
            preferencesFormFieldInputs={preferencesFormFieldInputs}
            onSubmitPreferences={onSubmitPreferences}
            onBackClicked={onBackClicked}
            isFetching={isFetching}
            isLoading={isSaving}
        />
    );
};
